import React, { useEffect, useState, ReactText, useContext, useRef } from 'react';
import {
  DualListBox,
  SearchableDropdown,
  SearchableDropdownOption,
  OptionPropType,
  Button,
  PageLoader,
  Notifier,
  notify,
} from '@scholastic/volume-react';
import { fetchInstitutions } from '../../../state/StatsReport.api';
import {
  GroupOptions,
  InstitutionListOptions,
  transformGroups,
  transformInstitutionList,
} from '../../StatsReport/StatsDataTransform';
import './IndividualSiteReports.scss';

import { ActionType, StoreContext } from '../../../state/store';
import { fetchChartData } from '../../../state/ReportsChart.api';
import { ReportLevels, sumData, GetNeededValueForView } from '../OnScreenReport.utils';
import { TABLE_HEADERS } from '../OverallSummaryReport/OverallSummaryReport.data';
import { CSVLink } from 'react-csv';
import { Redirect } from 'react-router-dom';

export interface ProductData {
  product: string;
  value: Array<School>;
}

export interface School {
  school: string;
  value: {
    reusltcode: number;
    result: { [key: string]: ReportLevels };
  };
}

const IndividualSiteReports = () => {
  const [tableView, setTableView] = useState<any>();

  const { state, dispatch } = useContext(StoreContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedInstitution, setSelectedInstitution] = useState<OptionPropType[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<SearchableDropdownOption>();
  const [availableOptions, setAvailableOptions] = useState<InstitutionListOptions[]>([]);
  const [availabledGroup, setAvailableGroup] = useState<GroupOptions[]>([]);
  const [masterSchoolList, setMasterSchoolList] = useState<InstitutionListOptions[]>([]);

  const [dataForDownload, setDataForDownload] = useState<any>([]);
  const [itemsAreLoaded, setItemsAreLoaded] = useState<boolean>(false);

  let prevUser = useRef({ selectedInstitution });

  const individualSiteRequestObj = state.individualSiteInfo;

  useEffect(() => {
    loadAvailableOptions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      prevUser.current.selectedInstitution.length !== 0 &&
      prevUser.current.selectedInstitution.length === selectedInstitution.length
    ) {
      notify.warning('Please select at least one Site to move');
    } else if (
      prevUser.current.selectedInstitution.length === 0 &&
      prevUser.current.selectedInstitution.length === selectedInstitution.length &&
      availableOptions.length !== 0
    ) {
      notify.warning('Please select at least one Site to move');
    } else {
      prevUser.current = { selectedInstitution };
    }
  }, [selectedInstitution]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedGroup) {
      const loadAvailableOptionsByGroup = (groupId: string | undefined) => {
        let institutionListByGroup: InstitutionListOptions[];
        if (groupId) {
          institutionListByGroup = masterSchoolList.filter((option) => option.group === groupId);
        } else {
          institutionListByGroup = masterSchoolList;
        }

        if (selectedInstitution.length > 0) {
          institutionListByGroup = institutionListByGroup.filter(
            (institution: { value: ReactText }) =>
              !selectedInstitution.find((selected) => selected.value === institution.value),
          );
        }
        setAvailableOptions(institutionListByGroup);
      };

      loadAvailableOptionsByGroup(selectedGroup.value);
    }
  }, [selectedGroup, masterSchoolList, selectedInstitution]);

  const loadAvailableOptions = async () => {
    if (individualSiteRequestObj) {
      // call api to fetch institution list
      const requestObj = {
        request: individualSiteRequestObj?.reportType,
        cuid: individualSiteRequestObj?.cuId,
      };

      try {
        const institutionList = await fetchInstitutions(requestObj);
        const availableShoolList = transformInstitutionList(institutionList.result.individualsites);

        setAvailableOptions(availableShoolList);
        setMasterSchoolList(availableShoolList);

        const availableGroupList = transformGroups(
          institutionList.result.groupcodes.filter((code: string) => code.trim() !== ''),
        );
        setAvailableGroup(availableGroupList);

        // TODO: We need to update DualListBox to fire event when available options are loaded.
        let element: HTMLElement = document.querySelector(
          '[aria-label="Move all right"]',
        ) as HTMLElement;
        element.click();
      } catch (err) {
        notify.warning('Something went wrong, please try again later');
        setIsLoading(false);
        console.error(err);
      }
    }
  };

  const createReport = async () => {
    setIsLoading(true);
    await getSummary();
    setIsLoading(false);
  };

  const getSummary = async () => {
    const chartData: any[] = [];

    try {
      for (let i = 0; i < individualSiteRequestObj.requestBody.products.length; i++) {
        let productChartData: any[] = [];
        let batchPromises: any[] = [];

        for (let j = 0; j < selectedInstitution.length; j++) {
          batchPromises.push(
            fetchChartData({
              request: 'usageSummary',
              cuid: individualSiteRequestObj.cuId,
              startDate: individualSiteRequestObj.requestBody.startDate,
              endDate: individualSiteRequestObj.requestBody.endDate,
              product: [individualSiteRequestObj.requestBody.products[i].value],
              customer: `${selectedInstitution[j].value}`,
            }).then((value) => ({
              school: selectedInstitution[j].label,
              value: value,
            })),
          );

          // Sends 10 Request at the same time.
          if (batchPromises.length === 10 || j === selectedInstitution.length - 1) {
            const batchValues = await Promise.all(batchPromises);
            productChartData = productChartData.concat(batchValues);
            batchPromises = [];
          }
        }

        const ChartDataForProduct = {
          product: individualSiteRequestObj.requestBody.products[i].label,
          value: productChartData,
        };

        chartData.push(ChartDataForProduct);
      }

      generateView(chartData);
      generateCSV(chartData);
    } catch (err) {
      notify.warning('Something went wrong, please try again later');
      console.error(err);
      setIsLoading(false);
    }
  };

  const generateCSV = (chartData: ProductData[]) => {
    const dataForDownload = chartData.map((product: ProductData) => {
      const schoolData = product.value.map((group: School) => {
        return {
          label: group.school,
          data: sumData(group.value.result, true),
        };
      });

      const schoolCSVData = schoolData.map((school) => {
        const intl = new Intl.NumberFormat();

        const individualDocuments = school.data.individual ? school.data.individual?.documents : 0;
        const individualSessions = school.data.individual ? school.data.individual?.sessions : 0;
        const individualSearches =
          typeof school.data.individual !== 'undefined' ? school.data.individual?.searches : 'N/A';

        return {
          name: school.label,
          product: product.product,
          onsiteDocuments: intl.format(school.data.onsite.documents),
          onsiteSessions: intl.format(school.data.onsite.sessions),
          onsiteSearches: GetNeededValueForView(school.data.onsite.searches),
          remoteDocuments: intl.format(school.data.remote.documents),
          remoteSessions: intl.format(school.data.remote.sessions),
          remoteSearches: GetNeededValueForView(school.data.remote.searches),
          individualDocuments: intl.format(individualDocuments || 0),
          individualSessions: intl.format(individualSessions || 0),
          individualSearches: GetNeededValueForView(individualSearches),
          totalDocuments: intl.format(school.data.total.documents),
          totalSessions: intl.format(school.data.total.sessions),
          totalSearches: GetNeededValueForView(school.data.total.searches),
        };
      });

      return schoolCSVData;
    });

    let mergedArrays: any[] = [];

    for (let i = 0; i < dataForDownload.length; i++) {
      mergedArrays = mergedArrays.concat(dataForDownload[i]);
    }

    setDataForDownload(mergedArrays);
    setItemsAreLoaded(true);

    if (state.individualSiteInfo.requestBody.outputType === 'Csv') {
      setTimeout(() => {
        let element: HTMLElement = document.querySelector(
          '.stats-isr__download-link',
        ) as HTMLElement;

        if (element) {
          element.click();
        }
      }, 10);
    }
  };

  const generateView = (chartData: ProductData[]) => {
    const tableHTML = chartData.map((product: ProductData) => {
      const groupData = product.value.map((school: School) => {
        return {
          label: school.school,
          data: sumData(school.value.result),
        };
      });

      const groupTableView = groupData.map((school) => {
        const intl = new Intl.NumberFormat();

        return (
          <tr>
            <td>{school.label}</td>
            <td>{intl.format(school.data.onsite.documents)}</td>
            <td>{intl.format(school.data.onsite.sessions)}</td>
            <td>{GetNeededValueForView(school.data.onsite.searches)}</td>
            <td>{intl.format(school.data.remote.documents)}</td>
            <td>{intl.format(school.data.remote.sessions)}</td>
            <td>{GetNeededValueForView(school.data.remote.searches)}</td>
            <td>{intl.format(school.data.total.documents)}</td>
            <td>{intl.format(school.data.total.sessions)}</td>
            <td>{GetNeededValueForView(school.data.total.searches)}</td>
          </tr>
        );
      });

      return (
        <div className="stats-isr__table-holder">
          <div className="stats-isr__table-title"> {product.product} </div>
          <div className="stats-isr__table">
            <table>
              <thead>
                <tr>
                  <th>School</th>
                  <th colSpan={3}>On-Site</th>
                  <th colSpan={3}>Remote</th>
                  <th colSpan={3}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td></td>
                  <td>Documents</td>
                  <td>Sessions</td>
                  <td>Searches</td>
                  <td>Documents</td>
                  <td>Sessions</td>
                  <td>Searches</td>
                  <td>Documents</td>
                  <td>Sessions</td>
                  <td>Searches</td>
                </tr>
                {groupTableView}
              </tbody>
            </table>
          </div>
        </div>
      );
    });

    setTableView(tableHTML);
  };

  const backToHome = () => {
    dispatch({ type: ActionType.SET_INDIVIDUAL_SITE_PAGE_INFO, data: null });
  };

  const print = () => {
    window.print();
  };

  if (!state.individualSiteInfo) return <Redirect to="/home" />;

  return (
    <div className="stats-isr">
      {isLoading && (
        <div>
          <PageLoader color={'dark'} size={'large'} type={'border'} />
        </div>
      )}

      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />
      <div className="stats-isr__tables-holder">
        <div className="stats-isr__buttons">
          {state.individualSiteInfo &&
            state.individualSiteInfo.outputType !== 'Csv' &&
            !!tableView && (
              <>
                <CSVLink
                  className="stats-isr__download-link float-right mt-2 vol-button vol-button--info"
                  data={dataForDownload}
                  headers={TABLE_HEADERS}
                  filename={'individual-site-report.csv'}
                >
                  {' '}
                  Download CSV{' '}
                </CSVLink>
                <span
                  className="stats-isr__link float-right mt-2 vol-button vol-button--info"
                  onClick={print}
                  id="print-report"
                >
                  {' '}
                  Print{' '}
                </span>
              </>
            )}
          <span
            className="stats-isr__link float-left mt-2 vol-button vol-button--info"
            onClick={backToHome}
            id="navigate-away"
          >
            {' '}
            Back To Report Selector{' '}
          </span>
          <div className="stats-isr__clearfix"></div>
        </div>
        <div id="section-to-print">
          {state.individualSiteInfo &&
            state.individualSiteInfo.outputType !== 'Csv' &&
            !!tableView && (
              <>
                <div className="stats-isr__table-title"> Individual Site Report </div>
                <div className="stats-isr__table-title">
                  {' '}
                  {state.individualSiteInfo?.requestBody.loggedInUserName} :{' '}
                  {state.individualSiteInfo?.requestBody.startDate} /{' '}
                  {state.individualSiteInfo?.requestBody.endDate}{' '}
                </div>
              </>
            )}
        </div>
      </div>

      {(!tableView ||
        (state.individualSiteInfo &&
          state.individualSiteInfo.requestBody.outputType === 'Csv')) && (
        <div>
          <div className="stats-isr__text-container">
            <span>
              <strong>
                You have chosen an Individual Site for{' '}
                {individualSiteRequestObj?.requestBody.productName} Starting{' '}
                {individualSiteRequestObj?.requestBody.startDate.toLocaleString('default', {
                  month: 'short',
                  year: 'numeric',
                })}
                .
              </strong>
              Select the sites you wish to include in your report and click Create Report button to
              continue.
            </span>
          </div>

          <div className="stats-isr__list-container">
            {availabledGroup.length > 1 && (
              <SearchableDropdown
                items={availabledGroup}
                placeholder="Filter by group code"
                onItemSelect={(item) => {
                  setSelectedGroup(item);
                }}
              />
            )}

            <DualListBox
              onSelection={(items) => setSelectedInstitution(items)}
              options={availableOptions}
              selectedOptionValues={selectedInstitution}
            />

            {state.individualSiteInfo && state.individualSiteInfo.requestBody.outputType !== 'Csv' && (
              <Button
                color="info"
                size="small"
                disabled={selectedInstitution.length === 0 || isLoading}
                className="float-right mt-2"
                onClick={createReport}
                id="create-report"
              >
                Create Report
              </Button>
            )}

            {state.individualSiteInfo && state.individualSiteInfo.requestBody.outputType === 'Csv' && (
              <Button
                color="info"
                size="small"
                disabled={selectedInstitution.length === 0 || isLoading}
                className="float-right mt-2"
                onClick={createReport}
                id="download-report"
              >
                Download Report
              </Button>
            )}

            {state.individualSiteInfo &&
              state.individualSiteInfo.requestBody.outputType === 'Csv' &&
              itemsAreLoaded && (
                <CSVLink
                  className="stats-isr__download-link stats-isr__download-link--hidden float-right mt-2 vol-button vol-button--info vol-button--small"
                  data={dataForDownload}
                  headers={TABLE_HEADERS}
                  filename={'individual-site-report.csv'}
                >
                  Hidden Download Link
                </CSVLink>
              )}

            <div className="stats-isr__clearfix"></div>
          </div>
        </div>
      )}
      <div className="stats-isr__list-container">
        <div className="stats-isr__tables-holder">
          {state.individualSiteInfo &&
            state.individualSiteInfo.requestBody.outputType !== 'Csv' &&
            tableView}
        </div>
      </div>
    </div>
  );
};

export default IndividualSiteReports;
